// Multi-session chat application.
// Session data flows through SessionContext; in-flight turns live in the
// app-wide ActiveStreamsContext (server-side background runs), which this
// component renders on top of the chat's settled messages and folds in
// once a turn ends.
import React, { useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MessageList, type MessageData } from './MessageList';
import { ChatInput, type ChatInputHandle } from './ChatInput';
import ChatTopBar from './ChatTopBar';
import { useLlmStatus } from './useLlmStatus';
import { type StreamChatBody } from '../../lib/sse';
import { isLiveStatus, useActiveStream, useActiveStreams } from '../../contexts/ActiveStreamsContext';
import { useSessionContext } from '../../contexts/SessionContext';
import { useGraphStorageContext } from '../../contexts/GraphStorageContext';
import { emptyAccumulatedGraph, clearUnpinned, mergeNeighborExpansion, deriveContextFromGraph } from '../../lib/graphMerge';
import { AccumulatedSubgraphPanel } from '../graph/AccumulatedSubgraphPanel';
import type {
  ChatMessage,
  ChatOptions,
  StoredMessage,
  AccumulatedGraph,
} from '../../types';
import { DEFAULT_CHAT_OPTIONS, normalizeChatMode } from '../../types';

const OPTIONS_KEY = 'malpedia-chat-options';

let msgIdCounter = 0;
const newId = () => `${Date.now()}-${++msgIdCounter}`;

function loadOptions(): ChatOptions {
  try {
    const raw = localStorage.getItem(OPTIONS_KEY);
    if (!raw) return { ...DEFAULT_CHAT_OPTIONS };
    const opts: ChatOptions = { ...DEFAULT_CHAT_OPTIONS, ...JSON.parse(raw) };
    const mode = normalizeChatMode(opts.mode);
    // 'llm_only' is the RAG / Chat pill's job; a stored one would silently
    // turn RAG mode into plain chat.
    return { ...opts, mode: mode === 'llm_only' ? 'auto' : mode };
  } catch {
    return { ...DEFAULT_CHAT_OPTIONS };
  }
}

function persistOptions(opts: ChatOptions) {
  try {
    localStorage.setItem(OPTIONS_KEY, JSON.stringify(opts));
  } catch {
    /* ignore */
  }
}

/** Convert StoredMessage[] from a session into MessageData[] for rendering. */
function storedToMessageData(stored: StoredMessage[]): MessageData[] {
  return stored.map((m) => ({
    id: m.id,
    role: m.role,
    content: m.content,
    sources: m.sources,
    completedSteps: m.completedSteps,
    stepDetails: m.stepDetails,
    planningEvent: m.planningEvent ?? null,
    routingEvent: m.routingEvent ?? null,
    graphTraversalEvent: m.graphTraversalEvent ?? null,
    profileEvent: m.profileEvent ?? null,
    scoreThreshold: m.scoreThreshold ?? null,
    queryDetectedIocs: m.queryDetectedIocs,
    responseDetectedIocs: m.responseDetectedIocs,
    turnId: m.turnId,
  }));
}

/** Convert MessageData[] to StoredMessage[] for persistence. */
function messageDataToStored(messages: MessageData[]): StoredMessage[] {
  return messages
    .filter((m) => !m.streaming)
    .map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
      sources: m.sources,
      completedSteps: m.completedSteps,
      stepDetails: m.stepDetails,
      planningEvent: m.planningEvent ?? null,
      routingEvent: m.routingEvent ?? null,
      graphTraversalEvent: m.graphTraversalEvent ?? null,
      profileEvent: m.profileEvent ?? null,
      scoreThreshold: m.scoreThreshold ?? null,
      queryDetectedIocs: m.queryDetectedIocs,
      responseDetectedIocs: m.responseDetectedIocs,
      turnId: m.turnId,
    }));
}

/** The chat's own messages followed by the in-flight turn from the stream
 *  registry. Drops the tail once the server copy of that turn has arrived
 *  (same turnId) and drops base entries the tail supersedes (the user
 *  message mirrored into a draft, matching ids after a re-attach). */
function withTail(base: MessageData[], tail: MessageData[] | undefined): MessageData[] {
  if (!tail || tail.length === 0) return base;
  const tailTurn = tail.find((m) => m.role === 'assistant')?.turnId;
  if (tailTurn && base.some((m) => m.turnId === tailTurn && !m.streaming)) return base;
  const ids = new Set(tail.map((m) => m.id));
  return [...base.filter((m) => !ids.has(m.id)), ...tail];
}

export default function ChatApp() {
  const ctx = useSessionContext();
  const { activeSession, activeSessionId } = ctx;
  const navigate = useNavigate();
  const { saveGraph } = useGraphStorageContext();

  // In-flight turns are owned by ActiveStreamsContext (app-wide) and run
  // server-side, so they survive chat switches, route changes and reloads.
  // Local state holds the chat's settled messages; the live turn is
  // rendered from the stream entry on top of it.
  const streams = useActiveStreams();
  const activeStream = useActiveStream(activeSessionId);
  const liveStream = activeStream && isLiveStatus(activeStream.status) ? activeStream : undefined;

  const [messages, setMessages] = useState<MessageData[]>(() =>
    activeSession ? storedToMessageData(activeSession.messages) : [],
  );
  const [useRag, setUseRag] = useState(true);
  const [chatOptions, setChatOptions] = useState<ChatOptions>(() => loadOptions());
  const [accGraph, setAccGraph] = useState<AccumulatedGraph>(() =>
    activeSession?.accumulatedGraph ?? emptyAccumulatedGraph(),
  );
  const [subgraphScope, setSubgraphScope] = useState<'none' | 'search_within' | 'expand_from'>('none');
  const [graphFullscreen, setGraphFullscreen] = useState(false);
  const [hasUnseenGraphData, setHasUnseenGraphData] = useState(false);
  const { stats, statsPending, llmStatus, llmOk } = useLlmStatus();

  const streaming = !!liveStream;
  const displayMessages = useMemo(
    () => withTail(messages, activeStream?.status === 'gone' ? undefined : activeStream?.messages),
    [messages, activeStream],
  );
  const displayGraph = liveStream ? liveStream.accGraph : accGraph;
  const displayHasUnseenGraphData = liveStream ? liveStream.hasUnseenGraphData : hasUnseenGraphData;

  const messagesRef = useRef(displayMessages);
  messagesRef.current = displayMessages;
  const accGraphRef = useRef(displayGraph);
  accGraphRef.current = displayGraph;
  const chatInputRef = useRef<ChatInputHandle>(null);

  // Track the session id we are currently viewing so the switch effect
  // can detect changes, and which id we've already populated state for
  // (server-backed sessions hydrate async via useChat; we need to fill
  // state when the detail resolves, not on switch alone).
  const prevSessionIdRef = useRef(activeSessionId);
  const initializedForIdRef = useRef<string | null>(activeSession ? activeSessionId : null);

  // Reset local state on session switch so the previous chat's data never
  // flashes. In-flight turns are NOT aborted: they keep running in the
  // background and toast when done.
  useEffect(() => {
    if (activeSessionId !== prevSessionIdRef.current) {
      prevSessionIdRef.current = activeSessionId;
      setMessages([]);
      setAccGraph(emptyAccumulatedGraph());
      setHasUnseenGraphData(false);
      initializedForIdRef.current = null;
    }
  }, [activeSessionId]);

  // Hydrate local state from activeSession when it resolves for the
  // current id. Idempotent: keyed by id so a re-render with the same
  // activeSession (or a background refetch) is a no-op.
  useEffect(() => {
    if (!activeSession || !activeSessionId) return;
    if (activeSession.id !== activeSessionId) return;
    if (initializedForIdRef.current === activeSessionId) return;
    setMessages(storedToMessageData(activeSession.messages));
    setAccGraph(activeSession.accumulatedGraph ?? emptyAccumulatedGraph());
    initializedForIdRef.current = activeSessionId;
  }, [activeSessionId, activeSession]);

  // A turn is running server-side for this chat but this tab isn't
  // following it (started in another tab, or before a reload) — attach.
  const activeRunId = activeSession?.id === activeSessionId ? activeSession?.activeRun?.runId : undefined;
  const activeRunQuery = activeSession?.activeRun?.query ?? '';
  useEffect(() => {
    if (!activeSessionId || !activeRunId || liveStream) return;
    streams.attachRun({ sessionId: activeSessionId, runId: activeRunId, query: activeRunQuery });
  }, [activeSessionId, activeRunId, activeRunQuery, liveStream, streams]);

  // The viewed chat's run ended: fold the finished turn into local state
  // and release the registry entry. Waits for hydration so the fold lands
  // on the chat's real history rather than the empty post-switch state.
  useLayoutEffect(() => {
    if (!activeStream || !activeSessionId || isLiveStatus(activeStream.status)) return;
    if (initializedForIdRef.current !== activeSessionId) return;
    if (activeStream.status === 'gone') {
      if (activeStream.serverMessages) setMessages(storedToMessageData(activeStream.serverMessages));
      if (activeStream.serverGraph) setAccGraph(activeStream.serverGraph);
    } else {
      setMessages((prev) => withTail(prev, activeStream.messages));
      setAccGraph(activeStream.accGraph);
      if (activeStream.hasUnseenGraphData) setHasUnseenGraphData(true);
    }
    streams.dismissStream(activeSessionId);
  }, [activeStream, activeSessionId, messages, streams]);

  useEffect(() => {
    persistOptions(chatOptions);
  }, [chatOptions]);

  // Pick up pending graph context from the graph explorer
  useEffect(() => {
    const pending = sessionStorage.getItem('malpedia-pending-graph-context');
    if (pending) {
      sessionStorage.removeItem('malpedia-pending-graph-context');
      chatInputRef.current?.setValue(pending);
    }
  }, []);

  const estimatedTokens = useMemo(
    () =>
      displayMessages
        .filter((m) => !m.streaming)
        .reduce((sum, m) => sum + Math.ceil(m.content.length / 4), 0),
    [displayMessages],
  );

  const buildHistory = (msgs: MessageData[]): ChatMessage[] =>
    msgs
      .filter((m) => !m.streaming)
      .map((m) => ({ role: m.role, content: m.content }));

  const handleChatOptionsChange = useCallback((partial: Partial<ChatOptions>) => {
    setChatOptions((prev) => ({ ...prev, ...partial }));
  }, []);

  const handleSend = useCallback(
    async (text: string) => {
      if (streaming) return;
      if (!activeSessionId) return;

      const effectiveMode = useRag ? chatOptions.mode : 'llm_only';
      const temporary = !!activeSession?.temporary;
      // The session id IS the server chat id (FastAPI adopts an unknown
      // client-minted id), so a running first turn can be re-attached by
      // the id the sidebar already shows.
      const chatId = temporary ? undefined : (activeSession?.serverChatId ?? activeSessionId);

      const userMsg: MessageData = { id: newId(), role: 'user', content: text };
      const assistantMsg: MessageData = {
        id: newId(),
        role: 'assistant',
        content: '',
        streaming: true,
        completedSteps: [],
      };

      const baselineMessages = messagesRef.current;
      const history = buildHistory(baselineMessages);

      const isNamedProfile = chatOptions.retrievalProfile !== 'auto' && chatOptions.retrievalProfile !== 'manual';
      const isManual = chatOptions.retrievalProfile === 'manual';

      const body: StreamChatBody = {
        message: text,
        history,
        chat_id: chatId,
        is_temporary: temporary || undefined,
        mode: effectiveMode === 'auto' ? undefined : effectiveMode,
        ...(isNamedProfile
          ? { retrieval_depth: chatOptions.retrievalProfile }
          : { top_k: chatOptions.topK }),
        ...(isManual && {
          custom_top_sources: chatOptions.customTopSources,
          custom_fusion_cap: chatOptions.customFusionCap,
          custom_max_chunks_per_doc: chatOptions.customMaxChunksPerDoc || undefined,
        }),
        ioc_pivot: chatOptions.iocPivot,
        web_search: chatOptions.webSearch || undefined,
        neighbor_expansion: chatOptions.neighborExpansion || undefined,
        full_doc_context: chatOptions.fullDocContext || undefined,
        ...(chatOptions.agentLoop !== 'default' && { agent: chatOptions.agentLoop === 'on' }),
        ...(chatOptions.contextLayout !== 'default' && { context_layout: chatOptions.contextLayout }),
      };

      // Inject graph-derived context from pinned nodes
      const { seed_entities, scope_cite_keys } = deriveContextFromGraph(accGraphRef.current, subgraphScope);
      if (seed_entities.length > 0) body.seed_entities = seed_entities;
      if (scope_cite_keys.length > 0) body.scope_cite_keys = scope_cite_keys;

      // Mirror the user message into a draft *now* so it is no longer
      // "empty" and switchSession won't prune it while the turn runs.
      // (No-op for server-backed chats.)
      ctx.updateSessionMessagesById(
        activeSessionId,
        messageDataToStored([...baselineMessages, userMsg]),
      );

      streams.startStream({
        sessionId: activeSessionId,
        body,
        initialAccGraph: accGraphRef.current,
        userMsg,
        assistantMsg,
        serverChatId: chatId,
        startSessionTitle: activeSession?.title,
        priorUserTurns: baselineMessages.filter((m) => m.role === 'user').length,
      });
    },
    [streaming, useRag, chatOptions, ctx, activeSession, activeSessionId, subgraphScope, streams],
  );

  // Hand-off from the landing page's Ask box: /chat?q=<question> opens a
  // fresh chat and sends the question there. The param is stripped at once
  // so a reload or Back doesn't re-send. The send waits until the new
  // session is active and its (empty) state has replaced the previous chat's
  // messages, so the question never lands in, or carries history from, the
  // chat that was open before. If the LLM is known to be down, the question
  // is only prefilled, like a manual send would be blocked.
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingAskRef = useRef<{ q: string; fromId: string | null } | null>(null);
  const { createSession } = ctx;
  useEffect(() => {
    const q = searchParams.get('q')?.trim();
    if (!q) return;
    const next = new URLSearchParams(searchParams);
    next.delete('q');
    setSearchParams(next, { replace: true });
    if (pendingAskRef.current) return;
    pendingAskRef.current = { q: q.slice(0, 2000), fromId: activeSessionId };
    createSession();
  }, [searchParams, setSearchParams, createSession, activeSessionId]);

  useEffect(() => {
    const ask = pendingAskRef.current;
    if (!ask || streaming || displayMessages.length > 0) return;
    if (!activeSessionId || activeSessionId === ask.fromId) return;
    if (!activeSession || activeSession.id !== activeSessionId || activeSession.messages.length > 0) return;
    pendingAskRef.current = null;
    if (llmStatus !== undefined && !llmOk) chatInputRef.current?.setValue(ask.q);
    else void handleSend(ask.q);
  }, [activeSession, activeSessionId, displayMessages.length, streaming, llmStatus, llmOk, handleSend]);

  // Stop cancels the run server-side; closing the stream alone would only
  // detach from it.
  const handleStop = useCallback(() => {
    if (activeSessionId) streams.stopStream(activeSessionId);
  }, [activeSessionId, streams]);

  const handleSuggestionClick = useCallback((query: string) => {
    chatInputRef.current?.setValue(query);
  }, []);

  // ── Graph management callbacks (same pattern as GraphPanel) ──────────

  const persistGraph = useCallback(
    (next: AccumulatedGraph) => {
      // While a turn runs, its stream owns the graph; route edits into it
      // so the end-of-turn save keeps them.
      if (liveStream && activeSessionId) {
        streams.updateStreamGraph(activeSessionId, next);
        return;
      }
      setAccGraph(next);
      if (activeSessionId) {
        const stored = messageDataToStored(messagesRef.current);
        ctx.updateSessionMessagesById(activeSessionId, stored, next);
      }
    },
    [liveStream, activeSessionId, ctx, streams],
  );

  const handleTogglePin = useCallback(
    (nodeId: string) => {
      const next: AccumulatedGraph = {
        ...displayGraph,
        nodes: displayGraph.nodes.map(n =>
          n.id === nodeId ? { ...n, pinned: !n.pinned } : n,
        ),
        lastUpdated: Date.now(),
      };
      persistGraph(next);
    },
    [displayGraph, persistGraph],
  );

  const handleClearUnpinned = useCallback(() => {
    persistGraph(clearUnpinned(displayGraph));
  }, [displayGraph, persistGraph]);

  const handleClearAll = useCallback(() => {
    persistGraph(emptyAccumulatedGraph());
  }, [persistGraph]);

  const handleNeighborExpansion = useCallback(
    (centerNodeId: string, nodes: any[], edges: any[]) => {
      const next = mergeNeighborExpansion(displayGraph, centerNodeId, nodes, edges);
      persistGraph(next);
    },
    [displayGraph, persistGraph],
  );

  const handleNodePositionChange = useCallback(
    (nodeId: string, pos: { x: number; y: number }) => {
      const next: AccumulatedGraph = {
        ...displayGraph,
        nodes: displayGraph.nodes.map(n =>
          n.id === nodeId ? { ...n, position: pos } : n,
        ),
        lastUpdated: Date.now(),
      };
      persistGraph(next);
    },
    [displayGraph, persistGraph],
  );

  const pinnedNodeCount = useMemo(
    () => displayGraph.nodes.filter(n => n.pinned).length,
    [displayGraph],
  );

  const handleSaveToGraphs = useCallback(() => {
    if (displayGraph.nodes.length === 0) {
      toast.error('Graph is empty — nothing to save');
      return;
    }
    const sessionTitle = activeSession?.title || 'Investigation';
    const stamp = new Date().toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    const id = saveGraph(displayGraph, `${sessionTitle} \u2014 ${stamp}`);
    toast.success(
      `Saved (${displayGraph.nodes.length} nodes)`,
      {
        action: {
          label: 'Open',
          onClick: () => navigate(`/graph?savedGraphId=${encodeURIComponent(id)}`),
        },
      },
    );
  }, [displayGraph, activeSession, saveGraph, navigate]);

  // Esc closes fullscreen overlay
  useEffect(() => {
    if (!graphFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setGraphFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [graphFullscreen]);

  const handleToggleGraph = useCallback(() => {
    setGraphFullscreen((open) => {
      const next = !open;
      if (next) {
        setHasUnseenGraphData(false);
        if (activeSessionId) streams.clearStreamUnseenGraph(activeSessionId);
      }
      return next;
    });
  }, [activeSessionId, streams]);

  const fullscreenOverlay = graphFullscreen
    ? ReactDOM.createPortal(
        <div className="malpedia-graph-fullscreen-overlay" role="dialog" aria-modal="true">
          <div className="malpedia-graph-fullscreen-header">
            <span className="malpedia-graph-fullscreen-title">Investigation Subgraph</span>
            <button
              className="malpedia-graph-fullscreen-close"
              onClick={() => setGraphFullscreen(false)}
              title="Close (Esc)"
              aria-label="Close fullscreen"
            >
              {'\u00D7'}
            </button>
          </div>
          <div className="malpedia-graph-fullscreen-body">
            <AccumulatedSubgraphPanel
              graph={displayGraph}
              onTogglePin={handleTogglePin}
              onClearUnpinned={handleClearUnpinned}
              onClearAll={handleClearAll}
              onNeighborExpansion={handleNeighborExpansion}
              onNodePositionChange={handleNodePositionChange}
              subgraphScope={subgraphScope}
              onSetScope={setSubgraphScope}
              messages={displayMessages as any}
              emptyMessage="Graph will populate as you chat."
              onSaveToGraphs={handleSaveToGraphs}
            />
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="malpedia-chat-container">
      <div className="malpedia-chat-main">
        <ChatTopBar
          onNewChat={ctx.createSession}
          onNewTempChat={ctx.createTemporarySession}
          onExport={() => ctx.openExportDialog()}
          isTemporary={activeSession?.temporary}
          isSensitive={activeSession?.serverIsSensitive}
        />
        <MessageList messages={displayMessages} onSuggestionClick={handleSuggestionClick} stats={stats} statsPending={statsPending} />
        <ChatInput
          ref={chatInputRef}
          onSend={handleSend}
          disabled={streaming || (llmStatus !== undefined && !llmOk)}
          streaming={streaming}
          llmStatus={llmStatus}
          onStop={handleStop}
          useRag={useRag}
          onToggleRag={() => setUseRag((v) => !v)}
          chatOptions={chatOptions}
          onChatOptionsChange={handleChatOptionsChange}
          estimatedTokens={estimatedTokens}
          maxContextTokens={chatOptions.maxContextTokens}
          graphOpen={graphFullscreen}
          onToggleGraph={handleToggleGraph}
          hasUnseenGraphData={displayHasUnseenGraphData}
          subgraphScope={subgraphScope}
          pinnedNodeCount={pinnedNodeCount}
        />
      </div>
      {fullscreenOverlay}
    </div>
  );
}
